import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mobileMediaQuery, themeColor, useBreakPoints } from '../../Theming';

const Container = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 60px 0 0;
  align-items: center;
  ${mobileMediaQuery} {
    grid-template-columns: 1fr;
    margin: 0;
  }
`;

const TextContainer = styled.div`
  width: 100%;
  align-items: center;
`;

const Header = styled.h3`
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0 0 60px 40px;
  ${mobileMediaQuery} {
    display: flex;
    justify-content: center;
    margin: 0 0 60px 0;
    font-size: 1.5rem;
  }
`;

const StyledList = styled.ol<{ selectedIndex: number; itemsCount: number }>`
  list-style-position: inside;
  margin: 0;
  padding: 0 0 0 40px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 4px;
    border-radius: 4px;
    background-color: ${themeColor('lightestGrey')};
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 4px;
    border-radius: 4px;
    background: linear-gradient(
      180deg,
      #ff4f65 0.07%,
      #f7c945 34.39%,
      #4dcafe 69.76%,
      #536efe 99.93%
    );
    clip-path: ${({ selectedIndex, itemsCount }) =>
      `inset(${(selectedIndex * 100) / itemsCount}% 0 ${
        100 - ((selectedIndex + 1) * 100) / itemsCount
      }% 0)`};
    transition: clip-path 0.15s ease-in-out;
  }
`;

const ListItem = styled.li<{ active: boolean }>`
  cursor: pointer;
  color: ${({ active }) => themeColor(active ? 'darkestGrey' : 'darkGrey')};
  transition: color 0.15s ease-in-out;

  &::marker {
    font-weight: bold;
  }
  & h4 {
    font-size: 1rem;
    font-weight: bold;
    margin: 0;
    display: inline;
  }
  & p {
    font-size: 1rem;
    line-height: 150%;
    margin: 10px 0 30px;
  }
`;

const Screenshot = styled.img`
  object-fit: cover;
  border: 1px solid ${themeColor('lightGrey')};
  border-radius: 6px;
  width: 100%;
  height: auto;
  max-width: 480px;
  max-height: 480px;
`;

interface ItemProps {
  onClick(index: number): void;
  index: number;
  selectedIndex: number;
}

const Item: React.FC<ItemProps> = ({
  onClick,
  index,
  selectedIndex,
  children,
}) => {
  return (
    <ListItem active={selectedIndex === index} onClick={() => onClick(index)}>
      {children}
    </ListItem>
  );
};

export interface HowItWorksItem {
  i18nKey: string;
  image: string;
}

interface ItemsProps {
  selectedIndex: number;
  items: HowItWorksItem[];
  onItemClicked(index: number): void;
}

const Items: React.FC<ItemsProps> = ({
  items,
  selectedIndex,
  onItemClicked,
}) => {
  const { t } = useTranslation();

  return (
    <StyledList selectedIndex={selectedIndex} itemsCount={items.length}>
      {items.map((item, i) => (
        <Item
          key={item.i18nKey}
          onClick={onItemClicked}
          index={i}
          selectedIndex={selectedIndex}
        >
          <h4>{t(`${item.i18nKey}.title`)}</h4>
          <p>{t(`${item.i18nKey}.text`)}</p>
        </Item>
      ))}
    </StyledList>
  );
};

interface HowItWorksProps {
  items: HowItWorksItem[];
  showAddButton?: boolean;
  buttonText?: string;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ items }) => {
  const { t } = useTranslation();
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const { mobile } = useBreakPoints();

  return (
    <Container>
      {!mobile && <Screenshot src={items[currentItemIndex].image} />}
      <TextContainer>
        <Header>{t('extension.howItWorks.header')}</Header>
        <Items
          items={items}
          selectedIndex={currentItemIndex}
          onItemClicked={(index) => setCurrentItemIndex(index)}
        />
      </TextContainer>
    </Container>
  );
};

export default HowItWorks;
